/**
 * Slot label utilities
 * Maps slot numbers to human-friendly labels with helper text and styling
 */

export const SLOT_CONFIG = {
  1: {
    label: 'Primary',
    helperText: 'Main preferred option',
    priority: 1,
    styles: {
      border: 'border-primary/40',
      bg: 'bg-primary/5',
      text: 'font-medium',
      helperColor: 'text-primary/70'
    }
  },
  2: {
    label: 'Secondary',
    helperText: 'Backup option',
    priority: 2,
    styles: {
      border: 'border-border',
      bg: 'bg-background',
      text: 'font-normal',
      helperColor: 'text-muted-foreground'
    }
  },
  3: {
    label: 'Tertiary',
    helperText: 'Final fallback option',
    priority: 3,
    styles: {
      border: 'border-border/60',
      bg: 'bg-muted/30',
      text: 'font-normal text-muted-foreground',
      helperColor: 'text-muted-foreground/70'
    }
  }
};

// Legacy compatibility
export const SLOT_LABELS = {
  1: 'Primary',
  2: 'Secondary', 
  3: 'Tertiary'
};

/**
 * Get human-friendly label for a slot number
 * @param {number|string} slot - Slot number (1, 2, or 3)
 * @returns {string} Human-friendly label (Primary, Secondary, or Tertiary)
 */
export const getSlotLabel = (slot) => {
  const slotNum = typeof slot === 'string' ? parseInt(slot, 10) : slot;
  return SLOT_CONFIG[slotNum]?.label || `Slot ${slot}`;
};

/**
 * Get helper text for a slot number
 * @param {number|string} slot - Slot number (1, 2, or 3)
 * @returns {string} Helper text describing the slot's purpose
 */
export const getSlotHelperText = (slot) => {
  const slotNum = typeof slot === 'string' ? parseInt(slot, 10) : slot;
  return SLOT_CONFIG[slotNum]?.helperText || '';
};

/**
 * Get full slot configuration
 * @param {number|string} slot - Slot number (1, 2, or 3)
 * @returns {object} Full configuration object with label, helperText, priority, styles
 */
export const getSlotConfig = (slot) => {
  const slotNum = typeof slot === 'string' ? parseInt(slot, 10) : slot;
  return SLOT_CONFIG[slotNum] || {
    label: `Slot ${slot}`,
    helperText: '',
    priority: slotNum,
    styles: {
      border: 'border-border',
      bg: 'bg-background',
      text: 'font-normal',
      helperColor: 'text-muted-foreground'
    }
  };
};

/**
 * Get slot styles for visual hierarchy
 * @param {number|string} slot - Slot number (1, 2, or 3)
 * @returns {object} Style classes for border, bg, text, helperColor
 */
export const getSlotStyles = (slot) => {
  const slotNum = typeof slot === 'string' ? parseInt(slot, 10) : slot;
  return SLOT_CONFIG[slotNum]?.styles || SLOT_CONFIG[2].styles;
};

/**
 * Get slot number from label
 * @param {string} label - Label (Primary, Secondary, or Tertiary)
 * @returns {number|null} Slot number or null if not found
 */
export const getSlotNumber = (label) => {
  const entries = Object.entries(SLOT_CONFIG);
  const found = entries.find(([_, config]) => config.label.toLowerCase() === label.toLowerCase());
  return found ? parseInt(found[0], 10) : null;
};

/**
 * Sort holds by priority (Primary first, then Secondary, then Tertiary)
 * @param {array} holds - Array of hold objects with slot property
 * @returns {array} Sorted array with Primary first
 */
export const sortHoldsByPriority = (holds) => {
  if (!holds || !Array.isArray(holds)) return [];
  return [...holds].sort((a, b) => {
    const priorityA = SLOT_CONFIG[a.slot]?.priority || a.slot;
    const priorityB = SLOT_CONFIG[b.slot]?.priority || b.slot;
    return priorityA - priorityB;
  });
};

/**
 * Get the primary (highest priority) active hold from an event
 * @param {object} event - Event object with hold1, hold2, hold3
 * @returns {object|null} The primary active hold or null
 */
export const getPrimaryActiveHold = (event) => {
  if (!event) return null;
  
  // Check in priority order
  for (const slot of [1, 2, 3]) {
    const hold = event[`hold${slot}`];
    if (hold && hold.status === 'Active') {
      return { ...hold, slot };
    }
  }
  return null;
};

/**
 * Get all active holds sorted by priority
 * @param {object} event - Event object with hold1, hold2, hold3
 * @returns {array} Array of active holds sorted by priority
 */
export const getActiveHoldsSorted = (event) => {
  if (!event) return [];
  
  const holds = [];
  for (const slot of [1, 2, 3]) {
    const hold = event[`hold${slot}`];
    if (hold && hold.status === 'Active') {
      holds.push({ ...hold, slot });
    }
  }
  return holds; // Already in priority order (1, 2, 3)
};

export default { 
  SLOT_CONFIG,
  SLOT_LABELS, 
  getSlotLabel, 
  getSlotHelperText,
  getSlotConfig,
  getSlotStyles,
  getSlotNumber,
  sortHoldsByPriority,
  getPrimaryActiveHold,
  getActiveHoldsSorted
};
